import { createStore } from 'vuex'
import axios from '../axios.js'
import businessLogic from '@/utils/businessLogic.js'
import config from 'sharedConfig'

const setRemotePreferenceFilters = () => {
    return config.default.remotePreferences.allRemotePreferences.map((preference) => ({
        id: preference,
        name: preference,
    }))
}

export const store = createStore({
    state: {
        allJobs: [],
        orgs: [],
        appliedFilters: [],
        availableFilters: {
            RemotePreference: setRemotePreferenceFilters(),
        },
        searchQuery: '',
        pendingAllJobs: false,
        pendingJobFunctions: false,
    },
    mutations: {
        addOrgs(state, data) {
            state.orgs = data
        },
        addJobs(state, data) {
            state.allJobs = data
        },
        updateSearchQuery(state, data) {
            state.searchQuery = data
        },
        setAppliedFilters(state, data) {
            state.appliedFilters = data
        },
        setPendingAllJobs(state, data) {
            state.pendingAllJobs = data
        },
        setPendingJobFunctions(state, data) {
            state.pendingJobFunctions = data
        },
        addJobFunctionFilters(state, data) {
            state.availableFilters.JobFunctions = data
        },
        addLocationFilters(state, data) {
            state.availableFilters.Locations = data
        },
        addSeniorityFilters(state, data) {
            state.availableFilters.Seniorities = data
        },
        addEmploymentTypeFilters(state, data) {
            state.availableFilters.EmploymentTypes = data
        },
    },
    actions: {
        fetchJobs(context) {
            context.commit('setPendingAllJobs', true)
            return axios.get('/public/all-jobs', {}).then((response) => {
                context.commit('addJobs', response.data.jobs)
                context.commit('addOrgs', response.data.orgList)

                const availableLocationFilters = []
                Object.values(context.getters.structuredAvailableLocations)
                    .sort((a, b) => {
                        return b.jobCount() - a.jobCount()
                    })
                    .forEach((countryLocation) => {
                        availableLocationFilters.push({
                            id: countryLocation.id,
                            name: countryLocation.name,
                            type: 'country',
                            jobCount: countryLocation.jobCount(),
                        })
                        if (
                            businessLogic.showAdminAreaFiltersUnderCountry(
                                countryLocation.jobCount(),
                            )
                        ) {
                            const adminAreasSortedAlphabetically = Object.values(
                                countryLocation.adminAreas,
                            ).sort((a, b) => {
                                return a.name.localeCompare(b.name)
                            })

                            availableLocationFilters.push(...adminAreasSortedAlphabetically)
                        }
                    })

                context.commit('addLocationFilters', availableLocationFilters)
                context.commit('setPendingAllJobs', false)
            })
        },
        fetchFilterItems(context) {
            context.dispatch('fetchJobFunctions')
            context.dispatch('fetchSeniorities')
            context.dispatch('fetchEmploymentTypes')
        },
        fetchJobFunctions(context) {
            context.commit('setPendingJobFunctions', true)
            axios.get('/public/job-functions', {}).then((response) => {
                context.commit('addJobFunctionFilters', response.data)
                context.commit('setPendingJobFunctions', false)
            })
        },
        fetchSeniorities(context) {
            return axios.get('/public/seniorities').then((response) => {
                context.commit(
                    'addSeniorityFilters',
                    response.data.map((seniority) => {
                        return {
                            id: seniority.name,
                            name: seniority.name,
                        }
                    }),
                )
            })
        },
        fetchEmploymentTypes(context) {
            return axios.get('/public/employment-types').then((response) => {
                context.commit(
                    'addEmploymentTypeFilters',
                    response.data.map((employmentType) => {
                        return {
                            id: employmentType.id,
                            name: employmentType.type,
                        }
                    }),
                )
            })
        },
    },
    getters: {
        // The following data structure construction could be moved to the backend if further performance optimisation is needed.
        structuredAvailableLocations(state) {
            if (!state.allJobs) return {}

            const result = {}

            state.allJobs.forEach((job) => {
                job.JobLocations.forEach((jobLocation) => {
                    const { country, adminArea } = jobLocation.Location
                    if (!result[country]) {
                        result[country] = {
                            id: country,
                            name: country,
                            type: 'country',
                            adminAreas: {},
                            jobIds: new Set(),
                            jobCount() {
                                return this.jobIds.size
                            },
                        }
                    }
                    result[country].jobIds.add(job.id)

                    if (adminArea) {
                        if (!result[country].adminAreas[adminArea]) {
                            const { adminArea, country } = jobLocation.Location
                            const uniqueId = `${adminArea}_${country}`

                            result[country].adminAreas[adminArea] = {
                                id: uniqueId,
                                name: adminArea,
                                type: 'adminArea',
                                adminArea,
                                country: country,
                                jobIds: new Set(),
                                jobCount() {
                                    return this.jobIds.size
                                },
                            }
                        }

                        result[country].adminAreas[adminArea].jobIds.add(job.id)
                    }
                })
            })

            return result
        },
        jobFilterResult: (state, getters) => {
            if (!state.appliedFilters.length) {
                return state.allJobs
            }

            const filterFunctions = {
                JobFunctions: (job, options) => {
                    return job.JobFunctions.some((jobFunction) =>
                        options.map((option) => option.id).includes(+jobFunction.FunctionId),
                    )
                },
                Locations: (job, options) => {
                    return job.JobLocations.some((jobLocation) => {
                        return options.find((option) => {
                            if (option.type === 'adminArea') {
                                return jobLocation.Location.adminArea === option.name
                            }

                            if (option.type === 'country') {
                                const countryJobCount =
                                    getters.structuredAvailableLocations[option.name].jobCount()

                                if (
                                    !businessLogic.showAdminAreaFiltersUnderCountry(countryJobCount)
                                ) {
                                    return jobLocation.Location.country === option.name
                                }

                                // This might be optimised by instead of making calculations here keeping
                                // track of this status in a property in the `option` when doing `handleOptionClick` in JobFilterDrawwer.
                                const noOfAdminAreasSelected = options.filter(
                                    (location) =>
                                        location.country === option.name &&
                                        location.type === 'adminArea',
                                ).length
                                const noOfAvailableAdminAreas = Object.values(
                                    getters.structuredAvailableLocations[option.name].adminAreas,
                                ).length

                                const allAdminAreasOfThisCountryAreSelected =
                                    noOfAdminAreasSelected === noOfAvailableAdminAreas

                                // Only when all admin areas of a country are selected do we include jobs that are specified with a country-wide location.
                                if (allAdminAreasOfThisCountryAreSelected) {
                                    return jobLocation.Location.country === option.name
                                }
                            }
                            return false
                        })
                    })
                },
                Seniorities: (job, options) => {
                    return job.JobSeniorities.some((seniority) =>
                        options.map((option) => option.name).includes(seniority.Seniority.name),
                    )
                },
                RemotePreference: (job, options) => {
                    return options.some((option) => option.id === job.remotePreferences)
                },
                EmploymentTypes: (job, options) => {
                    return job.JobEmploymentTypes.some((jobEmploymentType) =>
                        options
                            .map((option) => option.name)
                            .includes(jobEmploymentType.EmploymentType.type),
                    )
                },
            }

            const filteredJobs = state.allJobs.filter((job) => {
                return state.appliedFilters.reduce(
                    (passes, appliedFilter) =>
                        passes && filterFunctions[appliedFilter.name](job, appliedFilter.options),
                    true,
                )
            })

            return filteredJobs
        },
        jobQueryResult: (state, getters) => {
            const searchTerm = state.searchQuery.toLowerCase()
            if (!searchTerm) {
                return getters.jobFilterResult
            }
            const queriedJobs = state.allJobs.filter((job) => {
                const orgOrBrandName = job.orgName
                const searchItem = job.title + (orgOrBrandName || job.Org.name)
                return (
                    searchItem.toLowerCase().indexOf(searchTerm) !== -1 &&
                    getters.jobFilterResult.includes(job)
                )
            })

            return queriedJobs
        },
    },
    modules: {},
})
